import { Plus, Trash2, Link2, MousePointerClick, Send } from "lucide-react";
import { Switch } from "../ui/switch";
import { META_EVENTS } from "../../lib/tracking";

const inputCls = "w-full rounded-lg bg-white/[0.04] border hairline px-3 py-2 text-sm text-white outline-none focus:border-[#4fc3f7]/60";
const NEW = { enabled: true, meta_event: "ViewContent", custom_name: "", trigger: "pagina", url: "", match: "contiene", target: "", value: "", currency: "EUR", platforms: "meta" };
const TRIGGERS = { pagina: ["Visita di una pagina (URL)", Link2], click: ["Click su pulsante o link", MousePointerClick], invio_modulo: ["Invio modulo consulenza", Send] };
const PLATFORMS = [["meta", "Solo Meta Pixel"], ["tutte", "Meta + GA4 + GTM"], ["meta, ga4", "Meta + GA4"], ["ga4", "Solo GA4"], ["gtm", "Solo GTM"]];

const F = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-xs text-slate-400 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
  </label>
);

const Sel = ({ value, onChange, options, testId }) => (
  <select data-testid={testId} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v} className="bg-[#0e1528]">{l}</option>)}
  </select>
);

const EventCard = ({ ev, i, set, remove }) => {
  const e = { ...NEW, ...ev };
  const Icon = (TRIGGERS[e.trigger] || TRIGGERS.pagina)[1];
  const label = e.meta_event === "Custom" ? e.custom_name || "Evento personalizzato" : e.meta_event;
  return (
    <div data-testid={`event-card-${i}`} className={`rounded-2xl border hairline p-4 sm:p-5 space-y-4 ${e.enabled ? "bg-white/[0.03]" : "bg-white/[0.01] opacity-60"}`}>
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 grid place-items-center rounded-lg bg-[#3b6ef0]/15 text-[#8eabff]"><Icon className="w-4 h-4" /></span>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold truncate">{label}</p>
          <p className="text-xs text-slate-400 truncate">{TRIGGERS[e.trigger]?.[0]}{` · ${e.url || "tutte le pagine"}`}{e.trigger === "click" && e.target && ` · ${e.target}`}</p>
        </div>
        <Switch data-testid={`event-enabled-${i}`} checked={e.enabled} onCheckedChange={(v) => set({ enabled: v })} />
        <button type="button" data-testid={`event-delete-${i}`} onClick={remove} className="text-slate-400 hover:text-red-400" aria-label="Elimina evento"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Evento Meta">
          <Sel testId={`event-meta-${i}`} value={e.meta_event} onChange={(v) => set({ meta_event: v })}
            options={[...META_EVENTS.map(([k, l]) => [k, `${l} · ${k}`]), ["Custom", "Evento personalizzato…"]]} />
        </F>
        {e.meta_event === "Custom" ? (
          <F label="Nome evento personalizzato" hint="Senza spazi, es. ClickConsulenza">
            <input data-testid={`event-custom-${i}`} className={inputCls} value={e.custom_name} onChange={(x) => set({ custom_name: x.target.value.replace(/\s/g, "") })} />
          </F>
        ) : (
          <F label="Piattaforme"><Sel testId={`event-platforms-${i}`} value={e.platforms} onChange={(v) => set({ platforms: v })} options={PLATFORMS} /></F>
        )}
        <F label="Quando si attiva">
          <Sel testId={`event-trigger-${i}`} value={e.trigger} onChange={(v) => set({ trigger: v })} options={Object.entries(TRIGGERS).map(([k, [l]]) => [k, l])} />
        </F>
        {e.meta_event === "Custom" && <F label="Piattaforme"><Sel value={e.platforms} onChange={(v) => set({ platforms: v })} options={PLATFORMS} /></F>}
        {(
          <>
            <F label={e.trigger === "pagina" ? "URL della pagina" : "Solo su questa pagina (URL, facoltativo)"} hint="Incolla l'indirizzo completo o solo il percorso (es. /programma o una pagina futura). Vuoto = tutte le pagine.">
              <input data-testid={`event-url-${i}`} className={inputCls} value={e.url} placeholder="https://www.termonline.it/programma" onChange={(x) => set({ url: x.target.value })} />
            </F>
            <F label="Corrispondenza URL">
              <Sel testId={`event-match-${i}`} value={e.match} onChange={(v) => set({ match: v })} options={[["contiene", "L'URL contiene"], ["esatto", "L'URL è esattamente"]]} />
            </F>
          </>
        )}
        {e.trigger === "click" && (
          <F label="Elemento cliccato" hint="Testo del pulsante (es. Richiedi una consulenza) o inizio del link (https://wa.me, tel:, mailto:).">
            <input data-testid={`event-target-${i}`} className={inputCls} value={e.target} onChange={(x) => set({ target: x.target.value })} />
          </F>
        )}
        <F label="Valore (facoltativo)" hint="Usato ad esempio per Acquisto o Lead.">
          <div className="flex gap-2">
            <input data-testid={`event-value-${i}`} className={inputCls} value={e.value} placeholder="es. 50" onChange={(x) => set({ value: x.target.value })} />
            <select className={`${inputCls} w-24`} value={e.currency} onChange={(x) => set({ currency: x.target.value })}>
              {["EUR", "USD", "GBP", "CHF"].map((c) => <option key={c} className="bg-[#0e1528]">{c}</option>)}
            </select>
          </div>
        </F>
      </div>
    </div>
  );
};

export const EventsEditor = ({ value, onChange }) => {
  const list = Array.isArray(value) ? value : [];
  const set = (i) => (patch) => onChange(list.map((x, j) => (j === i ? { ...NEW, ...x, ...patch } : x)));
  return (
    <div data-testid="events-editor" className="space-y-3">
      {list.length === 0 && <p className="text-sm text-slate-400">Nessun evento configurato.</p>}
      {list.map((ev, i) => <EventCard key={i} ev={ev} i={i} set={set(i)} remove={() => onChange(list.filter((_, j) => j !== i))} />)}
      <button type="button" data-testid="event-add" onClick={() => onChange([...list, { ...NEW }])}
        className="h-10 px-4 rounded-full border border-[#ff5500]/50 text-sm text-white inline-flex items-center gap-2 hover:bg-[#ff5500]/10">
        <Plus className="w-4 h-4" />Aggiungi evento
      </button>
    </div>
  );
};
